import { responseRejectInterceptor } from "@api/base";
import { loanApi } from "@api/loans";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import { LoanListEntry } from "@modules/Loan/backoffice/LoanList/LoanListEntry";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { BackOfficeRoutes } from "@routes/urls";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import { Container, Grid, Header } from "semantic-ui-react";
import { SearchDateRange } from "./SearchDateRange";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import Qs from "qs";
import axios from "axios";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export class LoanSearch extends Component {
  modelName = "LOANS";

  searchApi = new InvenioSearchApi({
    axios: { url: loanApi.searchBaseURL, withCredentials: true },
    interceptors: { response: { reject: responseRejectInterceptor } },
  });
  getRequestSerializer = () => {
    const requestSerializer = this.searchApi.requestSerializer;
    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error(
        "RequestSerializer is not configured correctly in LoanSearch."
      );
    }
    return requestSerializer;
  };
  handleExportClick = async (format, size, currentQueryState) => {
    if (format === "pdf") {
      await this.exportToPDF(currentQueryState, size);
      return;
    }

    const requestSerializer = this.getRequestSerializer();
    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
    });
    const params = Qs.parse(queryString);
    params.page = 1;
    params.format = format;
    const exportUrl = `${loanApi.searchBaseURL}?${Qs.stringify(params, {
      arrayFormat: "repeat",
      encode: false,
    })}`;
    window.open(exportUrl, "_blank");
  };

  exportToPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params.page = 1;
      params.size = size;
      const exportUrl = `${loanApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const { data } = await axios.get(exportUrl, { withCredentials: true });
      const hits = data.hits?.hits || [];

      const tableRows = hits
        .map((hit, idx) => {
          const metadata = hit.metadata || {};
          const doc = metadata.document || {};
          const authors = Array.isArray(doc.authors)
            ? doc.authors.map((a) => a.full_name).join("; ")
            : doc.authors || "";
          return `
        <tr>
          <td>${idx + 1}</td>
          <td>${doc.title || ""}</td>
          <td>${authors}</td>
          <td>${doc.publication_year || ""}</td>
          <td>${doc.document_type || ""}</td>
        </tr>
      `;
        })
        .join("");

      const tableHeaders = `
      <th>#</th>
      <th>Title</th>
      <th>Author(s)</th>
      <th>Published Year</th>
      <th>Material Type</th>
    `;

      const htmlContent = generateExportHTML({
        reportTitle: "Loans Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        showUniversityLogo: true,
        showLrcLogo: true,
        fallbackLogoText: "Library Resource Center",
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }
    } catch (error) {
      console.error("PDF Export Error:", error);
    }
  };

  render() {
    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSorting = setReactSearchKitDefaultSortingOnEmptyQueryString(
      this.modelName
    );
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);
    const helperFields = [
      { name: "patron", field: "patron.name", defaultValue: '"Doe, John"' },
      {
        name: "title",
        field: "document.title",
        defaultValue: '"Little Prince"',
      },
    ];

    return (
      <>
        <Header as="h2">Loans and requests</Header>
        <OverridableContext.Provider value={{ ...SearchControlsOverridesMap }}>
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSorting}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="loans" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for loans"
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <Grid>
                          <Grid.Row
                            verticalAlign="middle"
                            className="search-clear-filters"
                          >
                            <Grid.Column width={16}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "1rem",
                                }}
                              >
                                <Header
                                  as="h4"
                                  content="Filter by"
                                  style={{ margin: 0 }}
                                />
                                <ClearFiltersButton module-identifier="loans" />
                              </div>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                        <SearchAggregationsCards modelName={this.modelName} />
                        <SearchDateRange
                          fromDateFilter="loans_from_date"
                          toDateFilter="loans_to_date"
                          title="Date"
                          subtitle="Loan start date"
                        />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid columns={2}>
                          <Grid.Column width={8} />
                          <Grid.Column width={8} textAlign="right">
                            <ExportReactSearchKitResults
                              exportBaseUrl={loanApi.searchBaseURL}
                              onExportClick={this.handleExportClick}
                              supportedFormats={["csv", "pdf"]}
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults
                          extraContent={
                            <NewButton
                              text="Add Document"
                              to={BackOfficeRoutes.documentCreate}
                            />
                          }
                        />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList ListEntryElement={LoanListEntry} />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
